import { FC } from 'react';
import { toast } from 'react-toastify';
import { captureException } from '@sentry/react';
import { SimpleModal } from 'src/components/Modal';
import { useI18nContext } from 'src/i18n/i18n-react';
import { Alert } from 'src/components/Alert';
import { AriaButton } from '../../../components/Button/Button';
import { trpc } from '../../../lib/trpc';

type Props = {
  integrationUuid: string;
  show: boolean;
  onClose: () => void;
};

const DeleteIntegrationModal: FC<Props> = ({
  integrationUuid,
  show,
  onClose,
}) => {
  const { LL } = useI18nContext();
  const trpcUtils = trpc.useUtils();

  const deleteIntegration = trpc.integrations.deleteIntegration.useMutation({
    onSuccess: () => {
      toast.success(LL.integrations.slack.disconnectSlack.success(), {
        className: 'toast-success',
      });
      void trpcUtils.integrations.getIntegrations.invalidate();
      void trpcUtils.user.getUserAndOrganisation.refetch();
      onClose();
    },
    onError: (e) => {
      captureException(e);
      toast.error(
        LL.integrations.slack.disconnectSlack.error({
          error: 'message' in e ? e.message : 'Unknown error',
        }),
        { className: 'toast-danger' }
      );
    },
  });

  return (
    <SimpleModal
      title={LL.integrations.slack.disconnectSlack.title()}
      show={show}
      onClose={deleteIntegration.isLoading ? null : onClose}
    >
      <div className="flex flex-col gap-3">
        {deleteIntegration.error && (
          <Alert type="error">
            {LL.errors.genericWithDetail({
              detail: deleteIntegration.error.message,
            })}
          </Alert>
        )}

        <AriaButton
          variant="primary"
          colour="danger"
          loading={deleteIntegration.isLoading}
          className="w-full"
          onPress={() => deleteIntegration.mutate({ uuid: integrationUuid })}
        >
          {LL.confirm()}
        </AriaButton>
      </div>
    </SimpleModal>
  );
};

export default DeleteIntegrationModal;
